import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "./data";
import { BrandLogo } from "./BrandLogo";
import { useSiteContent } from "../../context/SiteContent";

export const Navbar = ({ onQuote }) => {
  const { content } = useSiteContent();
  const ci = content?.companyInfo || {};
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const wa = `https://wa.me/${ci.whatsappNumber || "919998147181"}`;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`glass-nav fixed top-0 inset-x-0 z-50 transition-shadow duration-300 ${scrolled ? "shadow-[0_8px_30px_-12px_rgba(15,23,42,0.15)]" : ""}`}
      data-testid="navbar">
      <nav className="max-w-7xl mx-auto px-4 md:px-8 h-[68px] flex items-center justify-between gap-4">
        <a href="#top" onClick={(e) => { e.preventDefault(); go("#top"); }}
          className="flex items-center gap-2.5 shrink-0" data-testid="brand-logo">
          <BrandLogo size={ci.logoHeightPx ? Math.min(ci.logoHeightPx, 40) : 34} src={ci.logoUrl} />
        </a>

        <div className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <button key={l.href} onClick={() => go(l.href)}
              className="text-[13.5px] font-medium text-slate-600 hover:text-helios-slate transition-colors relative group"
              data-testid={`nav-${l.label.replace(/\s+/g, "-").toLowerCase()}`}>
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-[1.5px] bg-helios-cyan transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-2.5 shrink-0">
          <a href={ci.iotDashboardUrl || "#"} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-4 py-2 text-[13px] font-medium text-helios-slate hover:border-helios-slate hover:bg-helios-mist transition-colors"
            data-testid="cta-iot-dashboard">
            IoT Dashboard <ArrowUpRight size={14} />
          </a>
          <a href={wa} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-slate-300 text-emerald-600 hover:border-emerald-500 hover:bg-emerald-50 transition-colors"
            aria-label="WhatsApp" data-testid="cta-whatsapp">
            <MessageCircle size={16} />
          </a>
          <button onClick={onQuote}
            className="btn-sheen inline-flex items-center rounded-md bg-helios-slate px-5 py-2.5 text-[13px] font-semibold text-white hover:bg-helios-slate/90 transition-colors"
            data-testid="cta-request-quote">
            Request a Quote
          </button>
        </div>

        <button className="lg:hidden text-helios-slate" onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle" aria-label="Menu">
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} className="lg:hidden overflow-hidden bg-white border-t border-helios-line"
            data-testid="mobile-menu">
            <div className="px-6 py-5 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <button key={l.href} onClick={() => go(l.href)} className="text-left text-[15px] font-medium text-slate-700">
                  {l.label}
                </button>
              ))}
              <div className="flex flex-col gap-2.5 pt-3 border-t border-helios-line">
                <a href={ci.iotDashboardUrl || "#"} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 justify-center rounded-full border border-slate-300 px-4 py-2.5 text-sm font-medium">
                  IoT Dashboard <ArrowUpRight size={14} />
                </a>
                <a href={wa} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 justify-center rounded-full border border-slate-300 px-4 py-2.5 text-sm font-medium text-emerald-600">
                  <MessageCircle size={16} /> WhatsApp
                </a>
                <button onClick={() => { setMobileOpen(false); onQuote(); }}
                  className="rounded-md bg-helios-slate px-5 py-3 text-sm font-semibold text-white">
                  Request a Quote
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
